import * as angular from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';

import * as events from '../../../common/events';
import * as Fields from '../../../common/helpers/contextual';

import { DatesHelper } from '../../../common/datesHelper';
import { ComponentDefinition, Miniature, KeyValuePair, Application, ContextualField, PropertyType, GuidObjectType, IExtraIcon } from '../../../models';
import { IConnectionPointService, IApplicationService, ILoggerService, IAuthenticationService, IStoreActionsService } from '../../../services';
import { guidEmpty, Helper } from '../../../common';
import { getTimeZone } from '../../../common/getTimezone';
import { ApplicationViewModel } from '../applicationViewModel';

class WorkspaceContextualController implements angular.IComponentController {
    static forbiddenCharacters = ['<', '>', ':', '"', '/', '\\', '|', '?', '*', '+'];

    applications: Application[];
    connectionPoints: KeyValuePair<string, string>[];
    daysOfWeek: KeyValuePair<number, string>[];
    daysOfWeekWithEmpty: KeyValuePair<number, string>[];
    excludedGuidList: string[];
    extraIcon: IExtraIcon;
    formController: angular.IFormController;
    globalDisable: boolean = true;
    properties: ContextualField[];
    selectedItem: ApplicationViewModel;
    workspaceTypes: Miniature[];

    static $inject = [
        '$scope',
        '$rootScope',
        '$state',
        'storeActionsService',
        'applicationService',
        'connectionPointService',
        'loggerService'
    ];
    

    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: angular.IRootScopeService,
        private _$state: angular.ui.IStateService,
        private _storeActionsService: IStoreActionsService,
        private _applicationService: IApplicationService,
        private _connectionPointService: IConnectionPointService,
        private _loggerService: ILoggerService
    ) {
    }

    $onInit() {
        this.initMiniatureList();
        this.enumerateDays();
        this._$scope.$watch(() => moment.locale(), () => {
            this.enumerateDays();
            this.initProperties();
        });
        this._$scope.$watch(() => this.selectedItem, () => {
            const promise = this.selectedChange();
            if (promise) {
                promise
                    .then(() => {
                        this.initProperties();
                    })
                    .catch(() => {
                        this._loggerService.logError('Cannot load connection points');
                    });
            }
        });
        this.initProperties();
    }

    save() {
        const application = this.selectedItem.item1;
        if (!application.olsonTimeZoneName) {
            const helper = new DatesHelper();
            const timeZone = helper.getOlsonName('Local');
            application.olsonTimeZoneName = timeZone;
        }
        if (application && application.isChildApplicationCreationAllowed) {
            application.parentApplicationGuid = guidEmpty;
        }
        return this._applicationService
            .update(application)
            .then(data => {
                this._$rootScope.$broadcast(events.applicationSaved, data.application);
                this._loggerService.logSuccess('Application successfully updated');
            }).catch(reason => {
                this._loggerService.logError('Failed to save application');
            });
    }

    cancel() {
        this._$rootScope.$broadcast(events.applicationSaved, null);
        this.selectedItem = null;
    }

    validateName(value: string): { [key: string]: boolean } {
        const errorDuplicate = !_.some(this.applications, a => a.name.toLowerCase() === value.toLocaleLowerCase() && a.guid !== this.selectedItem.item1.guid);
        const errorForbidden = _.intersection(value, WorkspaceContextualController.forbiddenCharacters).length === 0;

        return Helper.getDictionnary<boolean>([
            { key: 'duplicate', value: errorDuplicate },
            { key: 'forbidden', value: errorForbidden },
            { key: 'forbidchar', value: errorForbidden }
        ]);
    }

    validateGuid(value: string): { [key: string]: boolean } {
        return Helper.getDictionnary<boolean>([
            { key: 'required', value: value !== guidEmpty },
        ]);
    }

    private initProperties() {
        const app = this.selectedItem?.item1;
        const backgroundField = angular.copy(Fields.Background);
        backgroundField.extraData.target = '#backgroundPickerModal';
        const nameField = angular.copy(Fields.Name);
        nameField.isValid = v => this.validateName(v);
        // ugly hack, doesn't work any other ways, + translation broke the text
        (nameField.messages[2] as any).extra = `(${WorkspaceContextualController.forbiddenCharacters.join(',')})`;
        const customField = angular.copy(Fields.CustomProperties);
        customField.extraData.type = GuidObjectType.Application;
        if (app) {
            this.excludedGuidList = [app.guid];
        }
        if (app && !app.parentApplicationGuid) {
            app.parentApplicationGuid = guidEmpty;
        }

        //const defaultDbType = 'mssql';
        const defaultDbType: string = 'npgsql';
        let dbLabel = '';
        switch (defaultDbType) {
            case 'npgsql':
                dbLabel = 'Default WiDE database';
                break;
            case 'mssql':
            default:
                dbLabel = 'Default WiDE database PostgreSQL';
                break;
        }

        const properties: ContextualField[] = [
            nameField,
            angular.copy(Fields.ShortComment),
            customField,
            {
                propertyType: PropertyType.Select,
                required: true,
                label: dbLabel,
                modelName: 'defaultConnectionPointGuid',
                propertyName: 'defaultConnectionPointGuid',
                icon: 'fa-database',
                disabled: !this.connectionPoints || this.connectionPoints.length === 0,
                isValid: (v) => this.validateGuid(v),
                extraData: {
                    options: this.connectionPoints
                }
            },
            {
                label: 'First day of production week',
                modelName: 'weekBeginDay',
                propertyName: 'weekBeginDayProperty',
                required: true,
                propertyType: PropertyType.Select,
                icon: 'fa-calendar-o',
                extraData: {
                    options: this.daysOfWeek
                }
            },
            {
                label: 'First day of calendar week',
                modelName: 'firstDayOfCalendarWeek',
                propertyName: 'FirstDayOfCalendarWeekProperty',
                required: false,
                propertyType: PropertyType.Select,
                icon: 'fa-calendar-times-o',
                extraData: {
                    options: this.daysOfWeekWithEmpty
                }
            },
            {
                modelName: 'dayBeginTime',
                propertyType: PropertyType.Time,
                propertyName: 'dayBeginTimeProperty',
                label: 'Start time of a day',
                icon: 'fa-clock-o',
                required: true,
                messages: [
                    { key: 'invalid', value: 'Invalid format for day begin time' },
                    { key: 'required', value: 'A day begin time is required' }
                ]
            },
            {
                modelName: 'olsonTimeZoneName',
                propertyType: PropertyType.Text,
                propertyName: 'olsonTimeZoneNameProperty',
                label: 'Timezone',
                disabled: true,
                icon: 'fa-globe',
                required: false
            },
            {
                modelName: 'validationEnabled',
                propertyType: PropertyType.Boolean,
                label: 'Enable approval',
                propertyName: 'validationEnabled',
                required: false,
                icon: '	fa-thumbs-o-up'
            },
            backgroundField,
            {
                modelName: 'ignoreErrors',
                propertyType: PropertyType.Boolean,
                label: 'Ignore errors',
                propertyName: 'ignoreErrorsProperty',
                required: false,
                icon: '	fa-eye-slash'
            },
            {
                modelName: 'parentApplicationGuid',
                propertyType: PropertyType.Application,
                label: 'Parent application',
                placeholder: 'Parent application',
                propertyName: 'parentApplicationProperty',
                required: false,
                icon: 'fa-external-link-square fa-rotate-270',
                hide: !!app?.isChildApplicationCreationAllowed,
                extraData: {
                    excludedGuidList: this.excludedGuidList
                },
                onChange: (c) => this.changeParentApplicationGuid()
            },
            {
                modelName: 'isChildApplicationCreationAllowed',
                propertyType: PropertyType.Boolean,
                label: 'Enable Child applications',
                hide: !!(app?.parentApplicationGuid && app?.parentApplicationGuid !== guidEmpty),
                placeholder: 'Enable Child applications',
                propertyName: 'isChildApplicationCreationAllowedProperty',
                icon: 'fa-external-link-square fa-rotate-90',
                onChange: (c) => this.changeChildApplicationCreationAllowed()
            },
            {
                modelName: 'computeAlarmsRanges',
                propertyType: PropertyType.Boolean,
                label: 'Compute alarms ranges',
                hide: false,
                placeholder: 'Compute alarms ranges',
                propertyName: 'computeAlarmsRangesProperty',
                icon: 'fa-bell-o'
            },
            {
                modelName: 'isLocked',
                propertyType: PropertyType.Boolean,
                label: 'Lock application',
                hide: false,
                placeholder: 'Lock application',
                propertyName: 'isLockedProperty',
                icon: 'fa-lock'
            }
        ];

        const appInfo = this.selectedItem?.item2;
        this.globalDisable = !app?.isIdleMode && (appInfo?.hasActiveJobs || appInfo?.hasActiveTasks);

        if (this.globalDisable) {
            this.extraIcon = {
                title: 'Application properties cannot be modified because there are active tasks',
                icon: 'fa-exclamation-triangle inline-warning',
                click: () => this.connectoToSelected()
            };
            properties.forEach(p => p.disabled = this.globalDisable || p.disabled);
        } else {
            this.extraIcon = undefined;
        }


        this.properties = properties;
    }

    private changeChildApplicationCreationAllowed() {
        if (this.selectedItem && this.selectedItem.item1) {
            const parentApplicationGuidProperty = _.find(this.properties, property => property.modelName === 'parentApplicationGuid');
            if (parentApplicationGuidProperty) {
                parentApplicationGuidProperty.hide = !this.selectedItem.item1.isChildApplicationCreationAllowed;
            }
        }
    }

    private changeParentApplicationGuid() {
        if (this.selectedItem && this.selectedItem.item1) {
            const isChildApplicationCreationAllowedProperty = _.find(this.properties, property => property.modelName === 'isChildApplicationCreationAllowed');
            if (isChildApplicationCreationAllowedProperty) {
                isChildApplicationCreationAllowedProperty.hide = !!(this.selectedItem && this.selectedItem.item1 && this.selectedItem.item1.parentApplicationGuid && this.selectedItem.item1.parentApplicationGuid !== guidEmpty);
            }
        }
    }

    private connectoToSelected(): void {
        this._storeActionsService.authentication
            .connectByApplicationName(this.selectedItem.item1.name, this.selectedItem.item1.guid)
            .then(() => this._$state.go('jobsDefinition'))
            .catch(() => this._loggerService.logError('Cannot longin user in selected application'));
    }

    private selectedChange() {
        if (this.selectedItem && this.selectedItem.item1) {
            const application = this.selectedItem.item1;
            if (!application.olsonTimeZoneName) {
                application.olsonTimeZoneName = getTimeZone();
                application.dayBeginTime = null;
            }
            return this._connectionPointService
                .getWideDatabasesForApplication(application.guid)
                .then(data => {
                    this.connectionPoints = data.map(d => {
                        return {
                            key: d.connectionPoint.guid,
                            value: d.connectionPoint.name
                        };
                    });
                    _.find(this.properties, p => p.propertyName === 'defaultConnectionPointGuid')
                        .extraData
                        .options = this.connectionPoints;
                });
        }
    }

    private enumerateDays(): any {
        this.daysOfWeek = moment.weekdays()
            .map((d, i) => { 
                return { key: i, value: d };
            });
        this.daysOfWeekWithEmpty = [{ key: null, value: '' }, ...this.daysOfWeek];
    }

    private initMiniatureList(): void {
        const list: Miniature[] = [];

        list.push(new Miniature('addWorkspace', 'Application', 'fa-plus',
            ['A new application', 'Let your creativity talk']));
        list.push(new Miniature('importApplication', 'Import application', 'fa-upload',
            ['Import an existing application']));

        this.workspaceTypes = list;
    }
}

export const wtWorkspaceContextual: ComponentDefinition = {
    componentId: 'wtWorkspaceContextual',
    controller: WorkspaceContextualController,
    template: require('./workspaceContextual.component.html'),
    controllerAs: 'vm',
    bindings: {
        selectedItem: '<',
        formController: '=',
        applications: '<'
    }
};